import React, { useMemo } from "react";
import categoryFieldsConfig from "./categoryFieldsConfig";
import "./CategoryFields.scss";

const getPureCategory = (category) =>
  category?.replace(/^[^\wА-Яа-яІіЇїЄє]+/, "").trim();

const LEGACY_ALIASES = {
  "Дім, Сад та затишок": "Дім та затишок",
  Антикваріат: "Аксесуари",
};

function resolveCategoryKey(inputName) {
  const pure = getPureCategory(inputName);
  if (!pure) return null;
  const aliased = LEGACY_ALIASES[pure] || pure;
  if (aliased && categoryFieldsConfig[aliased]) return aliased;

  if (
    aliased === "Для саду та подвір'я" &&
    (categoryFieldsConfig["Дім, Сад та затишок"] || categoryFieldsConfig["Дім та затишок"])
  ) {
    return categoryFieldsConfig["Дім та затишок"]
      ? "Дім та затишок"
      : "Дім, Сад та затишок";
  }
  if (
    aliased === "Дім та затишок" &&
    categoryFieldsConfig["Дім, Сад та затишок"]
  ) {
    return "Дім, Сад та затишок";
  }
  if (aliased === "Аксесуари" && categoryFieldsConfig["Антикваріат"]) {
    return "Антикваріат";
  }
  if (categoryFieldsConfig[pure]) return pure;
  return aliased;
}

const Select = ({ value, onChange, options, placeholder }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder || "Оберіть..."}</option>
    {options.map((opt) =>
      typeof opt === "string" ? (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ) : (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      )
    )}
  </select>
);

const MultiSelect = ({ value = [], onChange, options }) => {
  const toggle = (val) => {
    const set = new Set(value);
    set.has(val) ? set.delete(val) : set.add(val);
    onChange(Array.from(set));
  };
  return (
    <div className="multiselect">
      {options.map((opt) => {
        const v = typeof opt === "string" ? opt : opt.value;
        const l = typeof opt === "string" ? opt : opt.label;
        const checked = value.includes(v);
        return (
          <label key={v} className="ms-item">
            <input type="checkbox" checked={checked} onChange={() => toggle(v)} />
            <span>{l}</span>
          </label>
        );
      })}
    </div>
  );
};

const Checkbox = ({ value, onChange, label }) => (
  <label className="checkbox">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    <span>{label}</span>
  </label>
);

const CategoryFields = ({ category, formData, setFormData }) => {
  const attrs = formData?.attributes || {};
  const categoryKey = resolveCategoryKey(category);
  const source = categoryKey ? categoryFieldsConfig[categoryKey] : null;

  const fieldsRaw = useMemo(() => {
    if (!source) return [];
    if (typeof source === "function") return source(attrs);
    return Array.isArray(source) ? source : [];
  }, [source, attrs]);

  const fieldsUnique = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const f of fieldsRaw) {
      if (!f?.name || seen.has(f.name)) continue;
      seen.add(f.name);
      out.push(f);
    }
    return out;
  }, [fieldsRaw]);

  const fields = useMemo(() => {
    return fieldsUnique.filter((f) => {
      if (!f.showIf) return true;
      if (typeof f.showIf === "function") {
        try {
          return !!f.showIf(attrs);
        } catch {
          return true;
        }
      }
      return Object.entries(f.showIf).every(([k, v]) => attrs[k] === v);
    });
  }, [fieldsUnique, attrs]);

  const updateAttr = (name, val) => {
    setFormData((prev) => ({
      ...(prev || {}),
      attributes: { ...(prev?.attributes || {}), [name]: val },
    }));
  };

  if (!fields.length) return null;

  return (
    <div className="category-fields">
      <h3>🔹 Додаткова інформація</h3>
      {fields.map(({ name, label, type, options = [], required, placeholder, min, max, step, hint }) => {
        const value = attrs[name];
        return (
          <div className={`field cf-${type}`} key={name}>
            {type !== "checkbox" && (
              <label className="cf-label">
                {label} {required ? <span className="req">*</span> : null}
              </label>
            )}

            {type === "text" && (
              <input
                type="text"
                placeholder={placeholder || label}
                value={value ?? ""}
                onChange={(e) => updateAttr(name, e.target.value)}
                required={required}
              />
            )}

            {type === "number" && (
              <input
                type="number"
                placeholder={placeholder || label}
                value={value ?? ""}
                min={min ?? undefined}
                max={max ?? undefined}
                step={step ?? "any"}
                onChange={(e) => updateAttr(name, e.target.value)}
                required={required}
              />
            )}

            {type === "textarea" && (
              <textarea
                placeholder={placeholder || label}
                value={value ?? ""}
                onChange={(e) => updateAttr(name, e.target.value)}
                required={required}
              />
            )}

            {type === "select" && (
              <Select
                value={value}
                onChange={(v) => updateAttr(name, v)}
                options={options}
                placeholder={placeholder || "Оберіть..."}
              />
            )}

            {type === "multiselect" && (
              <>
                <div className="cf-sub">{label}</div>
                <MultiSelect
                  value={Array.isArray(value) ? value : []}
                  onChange={(vals) => updateAttr(name, vals)}
                  options={options}
                />
              </>
            )}

            {type === "checkbox" && (
              <Checkbox value={!!value} onChange={(v) => updateAttr(name, v)} label={label} />
            )}

            {hint && <div className="cf-hint">{hint}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryFields;
